import React, { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Mail, CalendarClock } from "lucide-react";
import { Section, Reveal, Overline } from "./Primitives";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import { CONTACT, PROJECT_TYPES, BUDGET_RANGES } from "../../data/content";

const initialState = { name: "", email: "", company: "", projectType: "", budget: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      setForm(initialState);
      toast.success("Request received — we'll be in touch within one business day.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "bg-black/20 border-white/10 text-white placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand h-12 rounded-xl";

  return (
    <Section id="contact" glow>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-brand/60" />
              <Overline>{CONTACT.overline}</Overline>
            </div>
          </Reveal>
          <Reveal delay={0.06}>
            <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter text-gradient">
              {CONTACT.title}
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-muted-foreground max-w-md">{CONTACT.sub}</p>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-10 space-y-4">
              <div className="flex items-center gap-4 text-white/80">
                <span className="flex items-center justify-center h-10 w-10 rounded-xl bg-brand/10 border border-brand/20 text-brand">
                  <CalendarClock className="h-5 w-5" strokeWidth={1.6} />
                </span>
                <span className="text-sm">Discovery call scheduled within one business day</span>
              </div>
              <div className="flex items-center gap-4 text-white/80">
                <span className="flex items-center justify-center h-10 w-10 rounded-xl bg-brand/10 border border-brand/20 text-brand">
                  <Mail className="h-5 w-5" strokeWidth={1.6} />
                </span>
                <span className="text-sm">Engineering-first, no sales runaround</span>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <div className="glass rounded-3xl p-8 md:p-10">
              {done ? (
                <div className="text-center py-14" data-testid="contact-success">
                  <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-2xl bg-brand/15 border border-brand/30 text-brand mb-6">
                    <ArrowUpRight className="h-7 w-7" />
                  </div>
                  <h3 className="font-heading text-2xl font-medium text-white">Request received.</h3>
                  <p className="mt-3 text-muted-foreground max-w-sm mx-auto">
                    Thanks for reaching out. Our team will get back to you within one business day to schedule your discovery call.
                  </p>
                  <button
                    onClick={() => setDone(false)}
                    data-testid="contact-reset"
                    className="mt-8 text-sm text-brand hover:text-brand-hover transition-colors"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-sm text-white/80">Name *</Label>
                      <Input id="name" data-testid="contact-name" value={form.name}
                        onChange={(e) => update("name", e.target.value)} placeholder="Jane Doe" className={inputCls} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm text-white/80">Email *</Label>
                      <Input id="email" type="email" data-testid="contact-email" value={form.email}
                        onChange={(e) => update("email", e.target.value)} placeholder="jane@company.com" className={inputCls} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-sm text-white/80">Company</Label>
                      <Input id="company" data-testid="contact-company" value={form.company}
                        onChange={(e) => update("company", e.target.value)} placeholder="Acme Inc." className={inputCls} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-white/80">Project type</Label>
                      <Select value={form.projectType} onValueChange={(v) => update("projectType", v)}>
                        <SelectTrigger data-testid="contact-project-type" className={inputCls}>
                          <SelectValue placeholder="Select a focus" />
                        </SelectTrigger>
                        <SelectContent className="bg-surface-2 border-white/10 text-white">
                          {PROJECT_TYPES.map((t) => (
                            <SelectItem key={t} value={t} className="focus:bg-white/10 focus:text-white">{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm text-white/80">Budget range</Label>
                    <Select value={form.budget} onValueChange={(v) => update("budget", v)}>
                      <SelectTrigger data-testid="contact-budget" className={inputCls}>
                        <SelectValue placeholder="Select a range" />
                      </SelectTrigger>
                      <SelectContent className="bg-surface-2 border-white/10 text-white">
                        {BUDGET_RANGES.map((b) => (
                          <SelectItem key={b} value={b} className="focus:bg-white/10 focus:text-white">{b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-sm text-white/80">What are you building? *</Label>
                    <Textarea id="message" data-testid="contact-message" value={form.message}
                      onChange={(e) => update("message", e.target.value)} rows={4}
                      placeholder="Tell us about the problem, the outcome you want, and any constraints."
                      className="bg-black/20 border-white/10 text-white placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-brand focus-visible:border-brand rounded-xl resize-none" />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="group w-full inline-flex items-center justify-center gap-2 rounded-xl bg-brand hover:bg-brand-hover text-white font-medium px-6 py-4 transition-colors duration-200 active:scale-[0.99] disabled:opacity-60"
                  >
                    {submitting ? (
                      <><Loader2 className="h-5 w-5 animate-spin" /> Sending…</>
                    ) : (
                      <>Book a Discovery Call
                        <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>
                    )}
                  </button>
                  <p className="text-xs text-muted-foreground text-center">
                    We respond within one business day. No spam, ever.
                  </p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </Section>
  );
};

export default Contact;
